import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/auth";
import type { PopupVariant } from "@/lib/popupTracking";

interface SubscribePopupContextType {
  openPopup: () => void;
}

const SubscribePopupContext = createContext<SubscribePopupContextType | undefined>(undefined);

// Graceful fallback: if a component renders before SubscribePopupProvider mounts
// (HMR edge cases / lazy boundary timing), return a no-op rather than crashing.
export const useSubscribePopup = () => {
  const ctx = useContext(SubscribePopupContext);
  return ctx ?? { openPopup: () => {} };
};

const SESSION_DISMISSED_KEY = "subscribe_popup_dismissed";
const TRIGGER_SHOWN_KEY = "subscribe_popup_trigger_shown_at";
const AB_VARIANT_KEY = "subscribe_popup_ab_variant";
const COOKIE_CONSENT_KEY = "cookie-consent";
const TRIGGER_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const MIN_TIME_ON_PAGE_MS = 10_000;
const MOBILE_SCROLL_VELOCITY_THRESHOLD = -1.2;
const SCROLL_DEPTH_THRESHOLD = 0.7;

type ABVariant = PopupVariant;

const getOrAssignVariant = (lockedWinner: ABVariant | null): { variant: ABVariant; isNew: boolean } => {
  if (lockedWinner) {
    const existing = localStorage.getItem(AB_VARIANT_KEY);
    if (existing !== lockedWinner) {
      localStorage.setItem(AB_VARIANT_KEY, lockedWinner);
    }
    return { variant: lockedWinner, isNew: existing !== lockedWinner };
  }
  const existing = localStorage.getItem(AB_VARIANT_KEY) as ABVariant | null;
  if (existing === "exit_intent" || existing === "scroll_depth") {
    return { variant: existing, isNew: false };
  }
  const variant: ABVariant = Math.random() < 0.5 ? "exit_intent" : "scroll_depth";
  localStorage.setItem(AB_VARIANT_KEY, variant);
  return { variant, isNew: true };
};

const isExcludedPath = (pathname: string) => {
  if (pathname.startsWith("/admin")) return true;
  if (/^\/[a-z]{2}\/community(\/|$)/.test(pathname)) return true;
  return false;
};

export const SubscribePopupProvider = ({ children, renderPopup }: { children: ReactNode; renderPopup: (open: boolean, onClose: () => void) => ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    return sessionStorage.getItem(SESSION_DISMISSED_KEY) === "true";
  });
  const { user } = useAuth();
  const location = useLocation();
  const triggeredRef = useRef(false);
  const variantRef = useRef<ABVariant | null>(null);
  const shownRef = useRef(false);

  const markShown = useCallback(
    (eventType: "popup_shown" | "popup_shown_fallback", reason: string) => {
      if (shownRef.current) return;
      shownRef.current = true;
      setOpen(true);
      if (variantRef.current) {
        // Lazy-load tracking to keep supabase out of the initial chunk
        void import("@/lib/popupTracking").then(({ trackPopupEvent }) => {
          trackPopupEvent(eventType, variantRef.current!, {
            reason,
            path: typeof window !== "undefined" ? window.location.pathname : null,
          });
        });
      }
    },
    []
  );

  // Auto-open fallback after 45s if no other trigger fired.
  // Pushed back from 7s so the popup is NOT the LCP candidate during
  // Lighthouse's lab run (~6s scenario), letting the hero be measured instead.
  useEffect(() => {
    if (dismissed) return;
    if (user) return;
    if (isExcludedPath(location.pathname)) return;
    const timer = setTimeout(() => {
      markShown("popup_shown_fallback", "fallback_45s");
    }, 45000);
    return () => clearTimeout(timer);
  }, [dismissed, user, location.pathname, markShown]);

  // A/B-tested trigger: exit_intent OR scroll_depth
  useEffect(() => {
    if (dismissed) return;
    if (user) return;
    if (isExcludedPath(location.pathname)) return;
    if (triggeredRef.current) return;

    const lastShownStr = localStorage.getItem(TRIGGER_SHOWN_KEY);
    if (lastShownStr) {
      const lastShown = parseInt(lastShownStr, 10);
      if (!Number.isNaN(lastShown) && Date.now() - lastShown < TRIGGER_COOLDOWN_MS) {
        return;
      }
    }

    let cancelled = false;
    let cleanup: (() => void) | null = null;
    let idleHandle: number | null = null;
    let idleTimeout: number | null = null;

    // Defer Supabase calls (fetchAbLock + variant_assigned tracking) out of the
    // critical request chain by lazy-importing them when the browser is idle.
    const runSetup = () => {
      if (cancelled) return;
      void (async () => {
        const [{ fetchAbLock }, { trackPopupEvent }] = await Promise.all([
          import("@/lib/abTestLock"),
          import("@/lib/popupTracking"),
        ]);
        const lock = await fetchAbLock();
        if (cancelled) return;

        const { variant, isNew } = getOrAssignVariant(lock?.winner ?? null);
        variantRef.current = variant;
        if (isNew) {
          void trackPopupEvent("variant_assigned", variant, { path: location.pathname });
        }

      const mountedAt = Date.now();

      const trigger = (reason: string) => {
        if (triggeredRef.current) return;
        if (Date.now() - mountedAt < MIN_TIME_ON_PAGE_MS) return;
        if (!localStorage.getItem(COOKIE_CONSENT_KEY)) return;
        triggeredRef.current = true;
        localStorage.setItem(TRIGGER_SHOWN_KEY, Date.now().toString());
        markShown("popup_shown", reason);
      };

      const handleMouseOut = (e: MouseEvent) => {
        if (e.relatedTarget === null && e.clientY <= 0) trigger("mouseout_top");
      };
      const handleMouseLeave = (e: MouseEvent) => {
        if (e.clientY <= 5) trigger("mouseleave_top");
      };
      let lastScrollY = window.scrollY;
      let lastScrollTime = performance.now();
      let mobileScrollScheduled = false;
      const handleMobileExitScroll = () => {
        if (mobileScrollScheduled) return;
        mobileScrollScheduled = true;
        requestAnimationFrame(() => {
          mobileScrollScheduled = false;
          const now = performance.now();
          const dy = window.scrollY - lastScrollY;
          const dt = now - lastScrollTime;
          if (dt > 0) {
            const velocity = dy / dt;
            if (velocity < MOBILE_SCROLL_VELOCITY_THRESHOLD && window.scrollY < 200) {
              trigger("mobile_scroll_up");
            }
          }
          lastScrollY = window.scrollY;
          lastScrollTime = now;
        });
      };

      let depthScrollScheduled = false;
      const handleScrollDepth = () => {
        if (depthScrollScheduled) return;
        depthScrollScheduled = true;
        requestAnimationFrame(() => {
          depthScrollScheduled = false;
          const scrollTop = window.scrollY;
          const docHeight = document.documentElement.scrollHeight - window.innerHeight;
          if (docHeight <= 0) return;
          const depth = scrollTop / docHeight;
          if (depth >= SCROLL_DEPTH_THRESHOLD) trigger("scroll_depth_70");
        });
      };

      if (variant === "exit_intent") {
        document.addEventListener("mouseout", handleMouseOut);
        document.documentElement.addEventListener("mouseleave", handleMouseLeave);
        window.addEventListener("scroll", handleMobileExitScroll, { passive: true });
      } else {
        window.addEventListener("scroll", handleScrollDepth, { passive: true });
      }

      cleanup = () => {
        document.removeEventListener("mouseout", handleMouseOut);
        document.documentElement.removeEventListener("mouseleave", handleMouseLeave);
        window.removeEventListener("scroll", handleMobileExitScroll);
        window.removeEventListener("scroll", handleScrollDepth);
      };
      })();
    };

    const ric = (window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    }).requestIdleCallback;
    if (typeof ric === "function") {
      idleHandle = ric(runSetup, { timeout: 3000 });
    } else {
      idleTimeout = window.setTimeout(runSetup, 1500);
    }

    return () => {
      cancelled = true;
      const cic = (window as unknown as {
        cancelIdleCallback?: (handle: number) => void;
      }).cancelIdleCallback;
      if (idleHandle != null && typeof cic === "function") cic(idleHandle);
      if (idleTimeout != null) clearTimeout(idleTimeout);
      cleanup?.();
    };
  }, [dismissed, user, location.pathname, markShown]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setDismissed(true);
    sessionStorage.setItem(SESSION_DISMISSED_KEY, "true");
    if (shownRef.current && variantRef.current) {
      void import("@/lib/popupTracking").then(({ trackPopupEvent }) => {
        trackPopupEvent("popup_dismissed", variantRef.current!);
      });
    }
  }, []);

  const openPopup = useCallback(() => {
    setOpen(true);
  }, []);

  return (
    <SubscribePopupContext.Provider value={{ openPopup }}>
      {children}
      {renderPopup(open, handleClose)}
    </SubscribePopupContext.Provider>
  );
};
